import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { openRemoteChannel, type RemoteChannelHandle } from '../services/presentationRemoteChannel';
import { isCommTarget, useCommHost } from './useCommHost';
import { commRandomId } from '../services/realtime/commClient';
import {
  type AnyRemoteEnvelope,
  type RemoteAckError,
  type RemoteBgmPick,
  type RemoteTtsSettings,
  type RemoteTtsVoiceInfo,
  type RemoteConnectionStatus,
  type RemoteEventType,
  type RemotePayloadMap,
  type RemotePresenceState,
  type RemoteQuestionType,
  type RemoteQuestionMode,
  type RemoteAnswerKey,
} from '../types/presentationRemote';
import {
  acquireRemoteSessionId,
  getRemoteDeviceId,
  randomId,
  sanitizeRemoteName,
} from '../utils/presentationRemoteIdentity';

export type RemoteCommandKind = 'notice' | 'question' | 'caption' | 'music' | 'tts';
export interface RemoteCommandLogEntry {
  id: string;
  kind: RemoteCommandKind;
  label: string;
  targetName: string;
  state: 'pending' | 'ok' | 'fail';
  error?: RemoteAckError | 'timeout' | 'no_target';
  at: number;
}
export interface RemoteAnswer {
  key: string;
  requestId: string;
  sessionId: string;
  deviceId: string;
  presentationName: string;
  content: string;
  answeredAt: number;
}
export interface RemoteActiveQuestion {
  requestId: string;
  text: string;
  questionType?: RemoteQuestionType;
  choices?: string[];
  mode?: RemoteQuestionMode;
  answerKey?: RemoteAnswerKey;
  revealAnswer?: boolean;
  targetSessionId: string;
  open: boolean;
}

const LOG_MAX = 8;
const ACK_TIMEOUT_MS = 6000;

const SELECTED_SS = 'cp-pres-remote-selected-v1';

/** userId가 있을 때만 채널 연결 (모달이면 열린 동안만 마운트) */
/** canUseRemote=false(프리)면 Supabase에 연결하지 않음 — 같은 브라우저만 */
export function usePresentationRemoteController(userId: string | null, canUseRemote = false) {
  const [status, setStatus] = useState<RemoteConnectionStatus>('idle');
  const [presence, setPresence] = useState<RemotePresenceState[]>([]);
  const [sessionId, setSessionId] = useState<string>('');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(() => {
    try {
      return window.sessionStorage.getItem(SELECTED_SS);
    } catch {
      return null;
    }
  });
  const handleRef = useRef<RemoteChannelHandle | null>(null);
  const [channelReady, setChannelReady] = useState(0);
  /** 제어 대상을 고르는 목록이 열려 있으면, 로컬을 쓰고 있어도 다른 기기를 찾는다 */
  const [targetPickerOpen, setTargetPickerOpen] = useState(false);
  const [log, setLog] = useState<RemoteCommandLogEntry[]>([]);
  const [question, setQuestion] = useState<RemoteActiveQuestion | null>(null);
  const [answers, setAnswers] = useState<RemoteAnswer[]>([]);
  const questionRef = useRef<RemoteActiveQuestion | null>(null);
  questionRef.current = question;
  const presenceRef = useRef<RemotePresenceState[]>([]);
  presenceRef.current = presence;

  const [tts, setTts] = useState<{
    sessionId: string;
    voices: RemoteTtsVoiceInfo[];
    total: number;
    koCount: number;
    includeAll: boolean;
    settings: RemoteTtsSettings;
  } | null>(null);

  const [slideState, setSlideState] = useState<{ sessionId: string; index: number; total: number; label?: string } | null>(null);
  const slideStatesRef = useRef(new Map<string, { sessionId: string; index: number; total: number; label?: string }>());

  /** 확인 요청에 응답하지 않은(닫혔는데 서버/전송 계층에 남은) 원격 프레젠 */
  const [unresponsive, setUnresponsive] = useState<ReadonlySet<string>>(() => new Set());
  const probeRef = useRef<{ at: number; pending: Set<string>; timer: number | null }>({ at: 0, pending: new Set(), timer: null });

  const onMessage = useCallback((msg: AnyRemoteEnvelope) => {
    if (msg.type === 'PRESENCE_PONG' || msg.type === 'SLIDE_STATE' || msg.type === 'PRESENTATION_STATUS') {
      const sid = msg.fromSessionId;
      probeRef.current.pending.delete(sid);
      setUnresponsive((cur) => {
        if (!cur.has(sid)) return cur;
        const next = new Set(cur);
        next.delete(sid);
        return next;
      });
      if (msg.type === 'PRESENCE_PONG') return;
    }
    if (msg.type === 'SLIDE_STATE') {
      const total = Math.floor(Number(msg.payload?.total));
      const index = Math.floor(Number(msg.payload?.index));
      if (!Number.isFinite(total) || !Number.isFinite(index) || total <= 0 || total > 10000) return;
      const label = typeof msg.payload?.label === 'string' ? msg.payload.label.slice(0, 20) : undefined;
      const st = { sessionId: msg.fromSessionId, index: Math.max(0, Math.min(index, total - 1)), total, label };
      slideStatesRef.current.set(msg.fromSessionId, st);
      setSlideState((cur) => (cur && cur.sessionId !== msg.fromSessionId ? cur : st));
      return;
    }
    if (msg.type === 'TTS_VOICES') {
      const p = msg.payload;
      setTts({
        sessionId: msg.fromSessionId,
        voices: Array.isArray(p?.voices) ? p.voices.slice(0, 50) : [],
        total: Number(p?.total) || 0,
        koCount: Number(p?.koCount) || 0,
        includeAll: !!p?.includeAll,
        settings: p.settings,
      });
      return;
    }
    if (msg.type !== 'SUBMIT_ANSWER') return;
    const q = questionRef.current;
    const a = msg.payload;
    /* 현재 열린 질문의 대상 프레젠이 보낸 응답만 */
    if (!q || !q.open || a?.requestId !== q.requestId) return;
    if (msg.fromSessionId !== q.targetSessionId || a.sessionId !== msg.fromSessionId) return;
    const content = String(a.content ?? '').trim().slice(0, 500);
    if (!content) return;
    /* 학생 기기 응답(수업 프레젠이 중계): 학생별 1개 — 다시 내면 교체 */
    const studentId = typeof a.studentId === 'string' ? a.studentId.slice(0, 64) : '';
    const key = studentId ? `${a.sessionId}:student:${studentId}` : `${a.sessionId}:${a.answeredAt}:${content}`;
    const name = studentId
      ? String(a.studentLabel ?? '').trim().slice(0, 40) || '학생'
      : presenceRef.current.find((p) => p.sessionId === a.sessionId)?.name ?? '프레젠테이션';
    setAnswers((prev) =>
      prev.some((x) => x.key === key && !studentId)
        ? prev
        : [
            ...prev.filter((x) => x.key !== key),
            {
              key,
              requestId: a.requestId,
              sessionId: a.sessionId,
              deviceId: String(a.deviceId ?? ''),
              presentationName: name,
              content,
              answeredAt: Number(a.answeredAt) || Date.now(),
            },
          ]
    );
  }, []);
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    let handle: RemoteChannelHandle | null = null;
    const onPageHide = () => handle?.close();
    void acquireRemoteSessionId().then((sid) => {
      if (cancelled) return;
      setSessionId(sid);
      const deviceId = getRemoteDeviceId();
      /* 처음엔 로컬(BroadcastChannel)만 — Supabase는 필요할 때만 연결 (아래 effect) */
      handle = openRemoteChannel({
        userId,
        sessionId: sid,
        remote: false,
        onStatus: setStatus,
        onPresence: setPresence,
        onMessage,
      });
      setChannelReady((n) => n + 1);
      handleRef.current = handle;
      handle.track({ role: 'controller', sessionId: sid, deviceId, name: '리모트', since: Date.now(), lastSeen: Date.now() });
      window.addEventListener('pagehide', onPageHide);
    });
    return () => {
      cancelled = true;
      window.removeEventListener('pagehide', onPageHide);
      handle?.close();
      handleRef.current = null;
      setPresence([]);
      setStatus('idle');
    };
  }, [userId, onMessage]);

  /** 원격 프레젠 생존 확인 1회 — 응답 없는 항목(창이 닫혔는데 끊김 알림이 안 온 소켓)은 목록에서 숨김. 주기 실행 없음 */
  const probePresentations = useCallback(() => {
    const h = handleRef.current;
    if (!h || !h.isRemoteEnabled()) return;
    const targets = h
      .getPresence()
      .filter((p) => p.role === 'presentation' && !p.isLocal && p.presentationKind !== 'lesson')
      .map((p) => p.sessionId);
    if (!targets.length) return;
    const pr = probeRef.current;
    if (pr.timer != null && Date.now() - pr.at < 3000) return;
    if (pr.timer != null) window.clearTimeout(pr.timer);
    pr.at = Date.now();
    pr.pending = new Set(targets);
    void h.send('PRESENCE_PING', { at: pr.at }).then((id) => {
      if (!id) {
        if (pr.timer != null) window.clearTimeout(pr.timer);
        pr.timer = null;
        return;
      }
    });
    pr.timer = window.setTimeout(() => {
      pr.timer = null;
      const dead = Array.from(pr.pending);
      pr.pending = new Set();
      if (!dead.length) return;
      setUnresponsive((cur) => {
        const next = new Set(cur);
        dead.forEach((s) => next.add(s));
        return next;
      });
    }, 4000);
  }, []);

  /* 원격 연결 직후(서버 roster 수신)·목록 열 때 1회 + 화면이 다시 보일 때 1회 확인 */
  useEffect(() => {
    if (status === 'connected' && targetPickerOpen) probePresentations();
  }, [status, targetPickerOpen, probePresentations]);
  useEffect(() => {
    if (status !== 'connected') return undefined;
    const t = window.setTimeout(probePresentations, 800);
    const onVis = () => {
      if (document.visibilityState === 'visible') probePresentations();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [status, probePresentations]);

  /* 확인 이후 다시 track(재접속 등)한 항목은 숨김 해제 */
  useEffect(() => {
    setUnresponsive((cur) => {
      if (!cur.size) return cur;
      const at = probeRef.current.at;
      const next = new Set(Array.from(cur).filter((sid) => presence.some((p) => p.sessionId === sid && !p.isLocal && (p.lastSeen ?? 0) <= at)));
      return next.size === cur.size ? cur : next;
    });
  }, [presence]);

  useEffect(
    () => () => {
      const t = probeRef.current.timer;
      if (t != null) window.clearTimeout(t);
    },
    []
  );

  const presentations = useMemo(
    () =>
      presence
        .filter((p) => p.role === 'presentation' && p.sessionId !== sessionId && (p.isLocal || !unresponsive.has(p.sessionId)))
        /* 수업 프레젠은 로컬 전용 — 이전 버전 탭이 온라인으로 등록해도 리모컨 목록에는 같은 기기 것만 */
        .filter((p) => p.presentationKind !== 'lesson' || p.isLocal)
        /* 원격 항목: leave(소켓 끊김) 또는 생존 확인(PRESENCE_PING) 무응답으로 제거. 로컬 항목은 전송 계층이 16초 기준 정리 */
        .sort((a, b) => (a.autoNumber ?? 999) - (b.autoNumber ?? 999) || a.since - b.since),
    [presence, sessionId, unresponsive]
  );

  const select = useCallback((id: string | null) => {
    setSelectedSessionId(id);
    try {
      if (id) window.sessionStorage.setItem(SELECTED_SS, id);
      else window.sessionStorage.removeItem(SELECTED_SS);
    } catch {
      /* ignore */
    }
  }, []);

  const rename = useCallback(async (targetSessionId: string, name: string): Promise<boolean> => {
    const h = handleRef.current;
    if (!h) return false;
    const ack = await h.sendWithAck(
      'RENAME_PRESENTATION',
      { name: sanitizeRemoteName(name) },
      { targetSessionId, timeoutMs: 5000 }
    );
    return !!ack?.ok;
  }, []);

  /* ---- 소통 프레젠테이션(가상 대상) ---- */
  const commSelected = isCommTarget(selectedSessionId);
  const comm = useCommHost(userId, canUseRemote, commSelected);
  const commPresence = useMemo<RemotePresenceState | null>(
    () =>
      comm.room && comm.targetId
        ? {
            role: 'presentation',
            sessionId: comm.targetId,
            deviceId: 'comm',
            name: `개별 디바이스 · ${comm.room.className}`,
            since: comm.room.createdAt,
            presentationKind: 'comm',
            title: comm.room.className,
          }
        : null,
    [comm.room, comm.targetId]
  );
  const realSelected = presentations.find((p) => p.sessionId === selectedSessionId) ?? null;
  const selectedOnline = commSelected
    ? !!commPresence && commPresence.sessionId === selectedSessionId && comm.connected
    : !!selectedSessionId && presentations.some((p) => p.sessionId === selectedSessionId);
  const selectedPresentation = commSelected
    ? commPresence && commPresence.sessionId === selectedSessionId
      ? commPresence
      : null
    : realSelected;
  const selectedRef = useRef<RemotePresenceState | null>(null);
  selectedRef.current = selectedPresentation;
  const commRef = useRef(comm);
  commRef.current = comm;
  const commLastNoticeRef = useRef<string>('');
  const commWaitRef = useRef(new Map<string, () => void>());

  /** 리모컨 명령 → 소통 방 프레임. 서버가 교사에게 돌려주는 item 으로 확인(ACK 대신) */
  const commCommand = useCallback(
    async (type: RemoteEventType, payload: unknown): Promise<{ ok: boolean; error?: RemoteCommandLogEntry['error'] }> => {
      const c = commRef.current;
      const p = (payload ?? {}) as Record<string, unknown>;
      let frame: Record<string, unknown>;
      let waitId = '';
      switch (type) {
        case 'SHOW_NOTICE': {
          const id = commRandomId();
          const d = Number(p.durationSec);
          frame = { t: 'notice', id, text: p.text, durationSec: Number.isFinite(d) && d > 0 ? d : null };
          commLastNoticeRef.current = id;
          waitId = id;
          break;
        }
        case 'CLOSE_NOTICE':
          if (!commLastNoticeRef.current) return { ok: true };
          frame = { t: 'close', id: commLastNoticeRef.current };
          commLastNoticeRef.current = '';
          break;
        case 'SHOW_QUESTION':
          /* 정답(answerKey)은 보내지 않음 — 채점은 리모컨(교사) 쪽 */
          frame = {
            t: 'question',
            id: p.requestId,
            text: p.text,
            questionType: p.questionType ?? 'short',
            ...(Array.isArray(p.choices) ? { choices: p.choices } : {}),
            /* 복수 정답 여부만 알림(정답 번호는 보내지 않음) */
            ...(p.multiSelect === true ? { multiSelect: true } : {}),
            src: 'remote',
          };
          waitId = String(p.requestId ?? '');
          break;
        case 'CLOSE_QUESTION':
          frame = { t: 'close', id: p.requestId };
          break;
        case 'SHOW_CAPTION':
          frame = { t: 'caption', id: p.id, text: p.text, final: p.final === true };
          break;
        case 'CLOSE_CAPTION':
          if (!p.id) return { ok: true };
          frame = { t: 'close', id: p.id };
          break;
        default:
          return { ok: false, error: 'not_implemented' };
      }
      if (!c.send(frame)) return { ok: false, error: 'timeout' };
      if (!waitId) return { ok: true };
      const ok = await new Promise<boolean>((resolve) => {
        const t = window.setTimeout(() => {
          commWaitRef.current.delete(waitId);
          resolve(false);
        }, ACK_TIMEOUT_MS);
        commWaitRef.current.set(waitId, () => {
          window.clearTimeout(t);
          commWaitRef.current.delete(waitId);
          resolve(true);
        });
      });
      return ok ? { ok: true } : { ok: false, error: 'timeout' };
    },
    []
  );
  const commCommandRef = useRef(commCommand);
  commCommandRef.current = commCommand;

  /* 학생 응답 → 기존 응답 목록(대화창)으로: 별칭이 프레젠 이름 자리에 표시 */
  comm.handlersRef.current = {
    onItem: (item) => commWaitRef.current.get(item.id)?.(),
    onAnswer: (requestId, a) => {
      const q = questionRef.current;
      if (!q || a?.memberId == null || q.requestId !== requestId || !isCommTarget(q.targetSessionId)) return;
      const content = String(a.content ?? '').trim().slice(0, 500);
      if (!content) return;
      setAnswers((prev) => [
        ...prev.filter((x) => !(x.requestId === requestId && x.sessionId === a.memberId)),
        {
          key: `${a.memberId}:${a.at}`,
          requestId,
          sessionId: a.memberId,
          deviceId: '',
          presentationName: a.nickname,
          content,
          answeredAt: Number(a.at) || Date.now(),
        },
      ]);
    },
    onWelcome: (_items, answers) => {
      const q = questionRef.current;
      if (!q || !isCommTarget(q.targetSessionId)) return;
      const list = answers[q.requestId];
      if (!list) return;
      setAnswers(
        list.map((a) => ({
          key: `${a.memberId}:${a.at}`,
          requestId: q.requestId,
          sessionId: a.memberId,
          deviceId: '',
          presentationName: a.nickname,
          content: a.content,
          answeredAt: a.at,
        }))
      );
    },
  };

  /**
   * 리모컨이 열려 있는 동안 다른 기기를 찾는다.
   * 이 기기(로컬)나 개별 디바이스에 붙으면 끊고, 대상 목록을 다시 열면 다시 찾는다.
   * 모달을 닫으면 이 훅이 언마운트되며 연결도 닫힌다.
   */
  const selectedIsLocalTransport = commSelected || !!realSelected?.isLocal;
  const remoteWanted = targetPickerOpen || !selectedIsLocalTransport;
  /** 원격이 필요하지만 유료가 아니어서 막힌 상태 */
  const remoteBlockedByPlan = remoteWanted && !canUseRemote;
  useEffect(() => {
    handleRef.current?.setRemoteEnabled(remoteWanted && canUseRemote);
  }, [remoteWanted, canUseRemote, channelReady]);

  /** 선택된 대상에게 명령 전송 — ACK 수신 시에만 성공, 타임아웃은 실패 */
  const sendCommand = useCallback(
    async <T extends RemoteEventType>(
      kind: RemoteCommandKind,
      label: string,
      type: T,
      payload: RemotePayloadMap[T],
      targetOverride?: string
    ): Promise<boolean> => {
      const target = targetOverride ?? selectedRef.current?.sessionId;
      const targetName =
        presenceRef.current.find((p) => p.sessionId === target)?.name ?? selectedRef.current?.name ?? '';
      const id = randomId();
      const push = (e: RemoteCommandLogEntry) =>
        setLog((prev) => [e, ...prev.filter((x) => x.id !== e.id)].slice(0, LOG_MAX));
      const base: RemoteCommandLogEntry = { id, kind, label, targetName, state: 'pending', at: Date.now() };
      if (isCommTarget(target)) {
        push(base);
        const r = await commCommandRef.current(type, payload);
        push({ ...base, state: r.ok ? 'ok' : 'fail', error: r.error, at: Date.now() });
        return r.ok;
      }
      const h = handleRef.current;
      if (!h || !target) {
        push({ ...base, state: 'fail', error: 'no_target' });
        return false;
      }
      push(base);
      const ack = await h.sendWithAck(type, payload, { targetSessionId: target, timeoutMs: ACK_TIMEOUT_MS });
      if (!ack) push({ ...base, state: 'fail', error: 'timeout', at: Date.now() });
      else push({ ...base, state: ack.ok ? 'ok' : 'fail', error: ack.ok ? undefined : ack.error, at: Date.now() });
      return !!ack?.ok;
    },
    []
  );

  const sendNotice = useCallback(
    (text: string, durationSec: RemotePayloadMap['SHOW_NOTICE']['durationSec'], speak = false) =>
      sendCommand('notice', speak ? '공지 전송(읽어주기)' : '공지 전송', 'SHOW_NOTICE', {
        text: text.trim().slice(0, 500),
        durationSec,
        speak,
      }),
    [sendCommand]
  );
  const closeNotice = useCallback(() => sendCommand('notice', '공지 닫기', 'CLOSE_NOTICE', {}), [sendCommand]);
  /** 자막: ACK 없이 바로 전송(누르는 동안 2초마다). 소통 방은 미지원 */
  const sendCaption = useCallback((id: string, text: string, final: boolean): boolean => {
    const target = selectedRef.current?.sessionId;
    const h = handleRef.current;
    if (target && isCommTarget(target)) {
      void commCommandRef.current('SHOW_CAPTION', { id, text: text.slice(0, 400), final });
      return !!commRef.current.room;
    }
    if (!h || !target) return false;
    void h.send('SHOW_CAPTION', { id, text: text.slice(0, 400), final }, { targetSessionId: target });
    return true;
  }, []);
  const closeCaption = useCallback((id?: string): boolean => {
    const target = selectedRef.current?.sessionId;
    const h = handleRef.current;
    if (target && isCommTarget(target)) {
      void commCommandRef.current('CLOSE_CAPTION', { id });
      return true;
    }
    if (!h || !target) return false;
    void h.send('CLOSE_CAPTION', id ? { id } : {}, { targetSessionId: target });
    return true;
  }, []);

  const askQuestion = useCallback(
    async (text: string, speak = false, opts?: {
        questionType?: RemoteQuestionType;
        choices?: string[];
        mode?: RemoteQuestionMode;
        answerKey?: RemoteAnswerKey;
        revealAnswer?: boolean;
      }) => {
      const questionType = opts?.questionType ?? 'short';
      const choices = questionType === 'choice' ? opts?.choices ?? [] : undefined;
      const mode: RemoteQuestionMode = opts?.mode === 'answer' && opts.answerKey ? 'answer' : 'submit';
      const answerKey = mode === 'answer' ? opts?.answerKey : undefined;
      const revealAnswer = mode === 'answer' ? opts?.revealAnswer !== false : undefined;
      /* 선택형 정답이 2개 이상이면 학생이 여러 개 골라 제출 */
      const multiSelect = questionType === 'choice' && (answerKey?.choiceIndexes?.length ?? 0) > 1;
      const target = selectedRef.current?.sessionId;
      if (!target) return sendCommand('question', '응답 요청', 'SHOW_QUESTION', { requestId: '', text });
      const requestId = randomId();
      const q: RemoteActiveQuestion = {
        requestId,
        text: text.trim().slice(0, 300),
        questionType,
        choices,
        mode,
        answerKey,
        revealAnswer,
        targetSessionId: target,
        open: true,
      };
      questionRef.current = q;
      setQuestion(q);
      setAnswers([]);
      const ok = await sendCommand(
        'question',
        speak ? '응답 요청(읽어주기)' : '응답 요청',
        'SHOW_QUESTION',
        {
          requestId,
          text: q.text,
          speak,
          questionType,
          ...(choices ? { choices } : {}),
          mode,
          ...(answerKey ? { answerKey } : {}),
          ...(multiSelect ? { multiSelect: true } : {}),
          ...(revealAnswer === false ? { revealAnswer: false } : {}),
        },
        target
      );
      if (!ok) {
        setQuestion((cur) => (cur?.requestId === requestId ? { ...cur, open: false } : cur));
      }
      return ok;
    },
    [sendCommand]
  );
  const closeQuestion = useCallback(async () => {
    const q = questionRef.current;
    if (!q) return false;
    const closed = { ...q, open: false };
    questionRef.current = closed;
    setQuestion(closed);
    return sendCommand('question', '응답 요청 종료', 'CLOSE_QUESTION', { requestId: q.requestId }, q.targetSessionId);
  }, [sendCommand]);

  const speakCurrent = useCallback(
    (targetKind: 'notice' | 'question' | 'caption', text?: string) =>
      sendCommand(
        targetKind === 'caption' ? 'caption' : targetKind,
        targetKind === 'notice' ? '공지 다시 읽기' : targetKind === 'question' ? '질문 다시 읽기' : '자막 읽기',
        'SPEAK_CURRENT',
        {
          targetKind,
          ...(targetKind === 'caption' && text ? { text: text.trim().slice(0, 400) } : {}),
        },
        targetKind === 'question' ? questionRef.current?.targetSessionId : undefined
      ),
    [sendCommand]
  );
  const requestTtsVoices = useCallback(
    (includeAll: boolean) => sendCommand('tts', '음성 목록 요청', 'GET_TTS_VOICES', { includeAll }),
    [sendCommand]
  );
  const setTtsSettings = useCallback(
    (settings: RemoteTtsSettings) => sendCommand('tts', '읽어주기 설정 적용', 'SET_TTS_SETTINGS', { settings }),
    [sendCommand]
  );
  const previewTts = useCallback(
    (text?: string) => sendCommand('tts', '미리듣기', 'TTS_PREVIEW', text ? { text } : {}),
    [sendCommand]
  );
  const musicPlay = useCallback(
    (pick?: RemoteBgmPick, label = '음악 재생', restart = false) =>
      sendCommand('music', label, 'MUSIC_PLAY', pick ? { pick, ...(restart ? { restart: true } : {}) } : {}),
    [sendCommand]
  );
  const musicPause = useCallback(() => sendCommand('music', '음악 일시정지', 'MUSIC_PAUSE', {}), [sendCommand]);
  const musicStop = useCallback(() => sendCommand('music', '음악 정지', 'MUSIC_STOP', {}), [sendCommand]);

  const reconnect = useCallback(() => handleRef.current?.reconnect(), []);

  /* 수업 프레젠 슬라이드 제어 */
  const selectedIsLesson = selectedPresentation?.presentationKind === 'lesson';
  const selectedSid = selectedPresentation?.sessionId ?? '';
  useEffect(() => {
    setSlideState(selectedSid ? slideStatesRef.current.get(selectedSid) ?? null : null);
    if (!selectedIsLesson || !selectedSid) return;
    /* 선택·재접속 시 현재 슬라이드 요청 */
    void handleRef.current?.send('SLIDE_NAV', { dir: 'sync' }, { targetSessionId: selectedSid });
  }, [selectedIsLesson, selectedSid, selectedOnline, status]);
  const slideNav = useCallback(
    (dir: 'prev' | 'next' | 'first' | 'last' | 'goto', index?: number) => {
      const target = selectedRef.current;
      if (!target || target.presentationKind !== 'lesson') return;
      void handleRef.current?.send('SLIDE_NAV', index != null ? { dir, index } : { dir }, { targetSessionId: target.sessionId });
    },
    []
  );
  const selectedSlideState = selectedIsLesson && slideState?.sessionId === selectedSid ? slideState : null;

  return {
    status,
    reconnect,
    comm,
    commSelected,
    slideNav,
    slideState: selectedSlideState,
    remoteWanted,
    remoteBlockedByPlan,
    remoteActive: remoteWanted && canUseRemote,
    setTargetPickerOpen,
    presentations,
    probePresentations,
    selectedSessionId,
    selectedOnline,
    selectedPresentation,
    select,
    rename,
    sessionId,
    handleRef,
    log,
    question,
    answers,
    sendNotice,
    closeNotice,
    sendCaption,
    closeCaption,
    askQuestion,
    closeQuestion,
    speakCurrent,
    tts: tts && tts.sessionId === selectedSessionId ? tts : null,
    requestTtsVoices,
    setTtsSettings,
    previewTts,
    musicPlay,
    musicPause,
    musicStop,
  };
}
